/* eslint-disable react/no-danger */
import dynamic from 'next/dynamic';
import { END } from 'redux-saga';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  actions,
  GET_DETAIL_PROJECT,
  GET_LIST_LANGUAGE,
} from '@containers/Home/slice';
import { wrapper, SagaStore } from 'store';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCurrentDomain,
  isCustomDomain as checkIsCustomDomain,
} from '@utils/domainHelper';
import fs from 'fs';
import path from 'path';
import {
  selectConfigLittlePlanetProject,
  selectConfigFirstSceneProject,
  selectFirstSceneMediaProject,
  selectGATagTrackingProject,
  selectThumbnailProject,
  selectGATrackingProject,
  selectListSceneByGroup,
  selectMetaDataProject,
  selectTitleProject,
  selectFeatureImageProject,
  selectDetailProject,
  selectConfigSoundProject,
  selectIsLoading,
} from '@containers/Home/selectors';
import Script from 'next/script';

const Home = dynamic(() => import('containers/Home'), {
  loading: () => <LoadingAnimation message="Đang khởi tạo tour..." />,
});

const ModalConfirm = dynamic(() => import('@components/ModalConfirm'));
const LoadingAnimation = dynamic(() => import('@components/LoadingAnimation'));

// Định nghĩa interface cho props của component
interface PublicContainerProps {
  hasData?: boolean;
}

function PublicContainer({ hasData = true }: PublicContainerProps) {
  const router = useRouter();
  const dispatch = useDispatch();
  const [visibleSoundConfirm, setVisibleSoundConfirm] = useState(false);

  const title = useSelector(selectTitleProject);
  const metadata = useSelector(selectMetaDataProject);
  const listScene = useSelector(selectListSceneByGroup);
  const thumbnail = useSelector(selectThumbnailProject);
  const ga_tracking = useSelector(selectGATrackingProject);
  const feature_image = useSelector(selectFeatureImageProject);
  const first_scene = useSelector(selectConfigFirstSceneProject);
  const ggtag_tracking = useSelector(selectGATagTrackingProject);
  const first_scene_media = useSelector(selectFirstSceneMediaProject);
  const config_little_planet = useSelector(selectConfigLittlePlanetProject);
  const detailProject = useSelector(selectDetailProject);
  const configSound = useSelector(selectConfigSoundProject);
  const isLoading = useSelector(selectIsLoading);

  const detailFirstScene = useMemo(
    () => listScene.filter(item => item.id === first_scene)[0] || undefined,
    [listScene, first_scene],
  );

  // Kiểm tra xem có âm thanh không
  const hasBackgroundSound = useMemo(() => {
    return !!detailProject?.background_sound;
  }, [detailProject]);

  // Xử lý khi đồng ý bật âm thanh
  const handleSoundConfirmOK = () => {
    dispatch(actions.setConfigSoundProject({ on: true }));
    setVisibleSoundConfirm(false);
  };

  // Xử lý khi từ chối bật âm thanh
  const handleSoundConfirmCancel = () => {
    dispatch(actions.setConfigSoundProject({ on: false }));
    setVisibleSoundConfirm(false);
  };

  // Hiển thị popup âm thanh khi trang được tải
  useEffect(() => {
    if (
      hasBackgroundSound &&
      (configSound?.on === undefined || configSound?.on === null)
    ) {
      console.log('[DEBUG] Showing sound confirmation from PublicContainer');
      setVisibleSoundConfirm(true);
    }
  }, [hasBackgroundSound, configSound]);

  const images = useMemo(() => {
    const imgs: any = [];
    if (thumbnail?.src)
      imgs.push({
        url: thumbnail?.src,
        width: 800,
        height: 600,
        alt: thumbnail?.name,
      });

    if (feature_image)
      imgs.push({
        url: feature_image,
        width: 800,
        height: 600,
        alt: '',
      });

    if (first_scene_media?.src)
      imgs.push({
        url:
          first_scene_media?.src_thumb ||
          first_scene_media?.src_preview ||
          first_scene_media?.src,
        width: 800,
        height: 600,
        alt: first_scene_media?.name,
      });

    return imgs;
  }, [thumbnail, feature_image, first_scene_media]);

  const hasDispatchedRef = useRef(false);
  const hasCalledApiRef = useRef(false);

  // Fetch project data on client-side based on domain detection
  useEffect(() => {
    if (typeof window !== 'undefined' && !hasCalledApiRef.current) {
      // Check if domain is passed as query parameter (for testing)
      const urlParams = new URLSearchParams(window.location.search);
      const domainParam = urlParams.get('domain');

      const currentDomain = getCurrentDomain();
      const isCustomDomainPage = checkIsCustomDomain(currentDomain);

      // Use domain from query parameter if provided, otherwise use current domain
      const domainToUse =
        domainParam || (isCustomDomainPage ? currentDomain : null);

      if (domainToUse) {
        console.log(
          '[Index] Fetching project with domain:',
          domainToUse,
          domainParam ? '(from query param)' : '(from hostname)',
        );
        hasCalledApiRef.current = true;
        dispatch(
          GET_DETAIL_PROJECT({
            domain: domainToUse,
            isPublic: true,
          }),
        );
      } else {
        console.log(
          '[Index] Main domain detected, using slug mode (no slug available on index page)',
        );
      }
    }
  }, [dispatch]);

  useEffect(() => {
    if (listScene && listScene.length && !hasDispatchedRef.current) {
      let scene = detailFirstScene
        ? { ...detailFirstScene }
        : { ...listScene[0] };

      // Nếu có query scene, sử dụng scene đó
      if (router.query.scene && router.query.scene[0]) {
        const selectedScene = listScene.filter(
          item => item.id === router.query.scene[0],
        )[0];
        scene = selectedScene ? { ...selectedScene } : { ...listScene[0] };
      }
      // Không cần phần tìm kiếm scene mặc định nữa vì đã xóa DEFAULT_SCENE_ID

      // NOTE: add intro little planet for first scene loaded
      scene.config = scene?.config || {};
      scene.config = {
        ...scene.config,
        isLittlePlanet: config_little_planet?.enable || false,
        timeTransitionLittlePlanet: config_little_planet?.transition_time || 2,
      };

      hasDispatchedRef.current = true; // Đánh dấu đã dispatch
      dispatch(actions.setSceneSelected(scene));
    }
  }, [listScene]);

  // Reset ref khi các dependencies khác thay đổi
  useEffect(() => {
    hasDispatchedRef.current = false;
  }, [router.query.scene, first_scene]);

  // Handle case when no data is available
  // When using custom domain mode or domain query param, data is loaded client-side, so check detailProject from Redux
  const urlParams =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null;
  const domainParam = urlParams?.get('domain');
  const currentDomain = getCurrentDomain();
  const isCustomDomainPage = checkIsCustomDomain(currentDomain);
  const hasDomainMode = domainParam || isCustomDomainPage;
  const shouldShowNoData = !hasData && !(hasDomainMode && detailProject);

  // Show loading indicator when fetching data
  if (isLoading && hasDomainMode) {
    return <LoadingAnimation message="Đang tải tour..." />;
  }

  if (shouldShowNoData) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
        }}
      >
        <h2>Không có dữ liệu tour</h2>
        <p>Vui lòng kiểm tra lại</p>
      </div>
    );
  }

  // Handle fallback state
  if (router.isFallback) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
        }}
      >
        <h2>Không có dữ liệu tour</h2>
        <p>Vui lòng kiểm tra lại</p>
      </div>
    );
  }

  return (
    <>
      <NextSeo
        title={metadata?.title || title}
        description={metadata?.description || 'Panoee - Virtual Tour 360'}
        openGraph={{
          type: 'website',
          url: process.env.NEXT_PUBLIC_BASE_URL + router.asPath,
          title: metadata?.title || title,
          description: metadata?.description || 'Panoee - Virtual Tour 360',
          images,
          site_name: 'Panoee Tour',
          profile: {
            username: router.query.username as string,
          },
        }}
        facebook={{
          appId: process.env.NEXT_PUBLIC_FB_APP_ID || '2086263614924092',
        }}
      />
      {ga_tracking && (
        <>
          <Script
            strategy="beforeInteractive"
            src={`https://www.googletagmanager.com/gtag/js?id=${ga_tracking}`}
          />
          <Script
            id="gg-tag"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${ga_tracking}', {
                  page_path: window.location.pathname,
                });
              `,
            }}
          />
        </>
      )}
      {ggtag_tracking && (
        <>
          <Script
            strategy="beforeInteractive"
            src={`https://www.googletagmanager.com/gtag/js?id=${ggtag_tracking}`}
          />
          <Script
            id="gg-tag-1"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());

                gtag('config', '${ggtag_tracking}');
              `,
            }}
          />
        </>
      )}
      <Home isPublic isPreventChangeUrl />

      {/* Thêm Modal xác nhận âm thanh trực tiếp ở đây */}
      {visibleSoundConfirm && (
        <ModalConfirm
          visible={visibleSoundConfirm}
          onOK={handleSoundConfirmOK}
          onCancel={handleSoundConfirmCancel}
        />
      )}
    </>
  );
}

export const getStaticProps = wrapper.getStaticProps(store => async () => {
  try {
    // Đọc dữ liệu từ file data.json
    const dataJsonPath = path.join(
      process.cwd(),
      'public',
      'mock-data',
      'data.json',
    );
    let hasData = false;
    if (fs.existsSync(dataJsonPath)) {
      const fileContent = fs.readFileSync(dataJsonPath, 'utf8');
      const jsonData = JSON.parse(fileContent);
      if (jsonData && jsonData.project) {
        store.dispatch(GET_DETAIL_PROJECT.success(jsonData.project));
        if (jsonData.languages && Array.isArray(jsonData.languages)) {
          store.dispatch(GET_LIST_LANGUAGE.success(jsonData.languages));
        }
        hasData = true;
      }
    }

    // Chờ saga hoàn thành
    store.dispatch(END);
    await (store as SagaStore).sagaTask?.toPromise();

    // Return props với trạng thái có dữ liệu hay không
    return {
      props: {
        hasData,
      },
      // Revalidate sau mỗi phút
      revalidate: 60,
    };
  } catch (error) {
    console.error('Error in getStaticProps:', error);
    return {
      props: {
        hasData: false,
      },
      revalidate: 30,
    };
  }
});

export default PublicContainer;
